import { Check } from '#components/Check'
import { MagicWandIcon } from '#components/MagicWandIcon'
import { Show } from '#components/Show'
import { Tag } from '#components/Tag'
import { UpgradeModal } from '#components/UpgradeModal'
import { decode, log, removeSpecialChars } from '#helpers'
import {
  endpoints,
  request,
  selectors,
  useCtas,
  usePop,
  useProductDetails,
  useSiteConfig,
  useTasks,
} from '#hooks'
import {
  Button,
  Flex,
  Icon,
  PanelBody,
  PanelRow,
  Spinner,
  __experimentalInputControl as InputControl,
  __experimentalInputControlSuffixWrapper as InputControlSuffixWrapper,
} from '@wordpress/components'
import { useEffect, useState } from '@wordpress/element'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const MetaDescription = ({ pop }) => {
  const { t } = useTranslation()

  const { tasks, isValidating, mutate } = useTasks(pop)
  const { post } = usePop(pop)
  const { availableCtas } = useCtas()
  const { productInfo } = useProductDetails()
  const { siteConfig } = useSiteConfig()

  const { watch, handleSubmit, control, setValue } = useForm({
    defaultValues: { description: '' },
  })

  const [isWaiting, setIsWaiting] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [triesLeft, setTriesLeft] = useState(siteConfig?.openai.triesLeft)
  const [isSameContent, setIsSameContent] = useState(false)
  const [hasMaxTries, setHasMaxTries] = useState(false)

  const hasAiFeature = tasks?.['nodescsingle'].aiFeature
  const aiSuggestions = productInfo?.features.aiSuggestions

  const description = post?.postMetaFields?.[selectors.description]?.[0] || ''
  const keyword = post?.postMetaFields?.[selectors.keyword]?.[0]

  const condition = tasks?.['nodescsingle'].pass
  const isKeywordInDesc =
    tasks?.['keyontitle'].pass && description.includes(keyword)

  const upgradeCta = availableCtas?.find(
    (cta) => cta.id === 'wordpress-upgrade'
  )

  const MIN_LENGTH = 70
  const MAX_LENGTH = 160

  const hasAppropiateLength =
    watch('description').length >= MIN_LENGTH &&
    watch('description').length <= MAX_LENGTH

  const openUpgradeModal = () => {
    setShowUpgradeModal(true)
  }
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false)
  }

  const updateWhenValue = () => {
    if (post) {
      setValue('description', decode(description) || '')
    }
  }

  const onGenerateDescription = () => {
    setIsWaiting(true)
    request(
      endpoints.metatagsGenerator.create({
        url: post.link,
        ...(keyword && {
          keyword,
        }),
      })
    )
      .then((response) => {
        if (response.data.errors) {
          if (response.data.errors[0].code === 403) {
            setHasMaxTries(true)
            setIsSameContent(false)
            setTriesLeft(0)
          }
          throw new Error(
            `Error code ${response.data.errors[0].code}: ${response.data.errors[0].title}`
          )
        }
        if (
          removeSpecialChars(response.data.response.description) ===
          removeSpecialChars(watch('description'))
        ) {
          setIsSameContent(true)
        } else {
          setIsSameContent(false)
          setValue('description', response.data.response.description)
        }
        setTriesLeft(response.data.triesLeft)
      })
      .finally(() => {
        setIsWaiting(false)
      })
      .catch(log)
  }

  const sendDescription = (data) => {
    request(endpoints.meta.create({ postId: pop.id, data }))
      .then(() => {
        mutate()
      })
      .catch((error) => {
        console.log(error)
      })
  }

  useEffect(updateWhenValue, [post])

  return (
    <PanelBody
      title={t('sidebar.task.metadescription.title')}
      icon={isValidating ? <Spinner /> : <Check condition={condition} />}
      initialOpen={!condition}
    >
      <PanelRow>
        <form
          className="meta-description"
          onSubmit={handleSubmit(sendDescription)}
        >
          <p>{t('sidebar.metadescription.intro')}</p>
          <ul>
            <li>
              <Flex justify="flex-start">
                <Check condition={hasAppropiateLength} />
                <span>
                  {t('sidebar.task.metadescription.request1', {
                    minChars: MIN_LENGTH,
                    maxChars: MAX_LENGTH,
                  })}
                </span>
              </Flex>
            </li>
            <li>
              <Flex justify="flex-start">
                <Check condition={isKeywordInDesc} />
                <span>{t('sidebar.task.metadescription.request2')}</span>
              </Flex>
            </li>
          </ul>
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <InputControl
                help={
                  <Flex wrap={true} align="flex-start" gap="1">
                    <span
                      className={`meta-description__input-help${
                        isSameContent || hasMaxTries
                          ? ' meta-description__input-help--error'
                          : ''
                      }`}
                    >
                      {t('sidebar.task.addtitle.field.helper.counter', {
                        tries: aiSuggestions - triesLeft || 0,
                        maxtries: aiSuggestions,
                      }) + ' '}
                      <Show when={isSameContent}>
                        {t('sidebar.task.addtitle.field.helper.samecontent')}
                      </Show>
                      <Show when={hasMaxTries}>
                        {t('sidebar.task.addtitle.field.helper.maxtries')}
                      </Show>
                    </span>
                    <span>{`${
                      watch('description').length
                    }/${MAX_LENGTH}`}</span>
                  </Flex>
                }
                label={
                  <Flex justify="flex-start">
                    <span>{t('sidebar.task.metadescription.field.title')}</span>
                    <Show when={hasAiFeature}>
                      <Tag variant="pro">
                        {t('sidebar.task.addtitle.tag.ai')}
                      </Tag>
                    </Show>
                  </Flex>
                }
                placeholder={t(
                  'sidebar.task.metadescription.field.placeholder'
                )}
                suffix={
                  <Show when={hasAiFeature}>
                    <InputControlSuffixWrapper
                      onClick={
                        upgradeCta ? openUpgradeModal : onGenerateDescription
                      }
                    >
                      {isWaiting ? (
                        <Spinner style={{ color: '#AC18B4' }} />
                      ) : (
                        <Icon
                          style={{ cursor: 'pointer' }}
                          icon={<MagicWandIcon />}
                        />
                      )}
                    </InputControlSuffixWrapper>
                  </Show>
                }
                {...field}
              />
            )}
          />
          <Button
            type="submit"
            variant="primary"
            isBusy={isWaiting}
            disabled={isWaiting || isValidating}
          >
            {t('sidebar.metadescription.cta')}
          </Button>
        </form>
        <Show when={showUpgradeModal}>
          <UpgradeModal
            closeModal={closeUpgradeModal}
            cta={upgradeCta?.attributes}
          />
        </Show>
      </PanelRow>
    </PanelBody>
  )
}
